import { FC } from 'react';
import { Encounter, Patient, Practitioner } from '@medplum/fhirtypes';
import {
  Card,
  Stack,
  Group,
  Badge,
  Divider,
  Box,
  Text,
  LoadingOverlay,
  List,
} from '@mantine/core';
import {
  IconUser,
  IconCalendar,
  IconClipboardText,
  IconNotes,
  IconActivity,
  IconStethoscope,
} from '@tabler/icons-react';
import { formatHumanName } from '@medplum/core';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { StatusBadge } from '@/components/medplum-react/StatusBadge/StatusBadge';

interface EncounterDetailsCardProps {
  encounter: Encounter;
  patient?: Patient | null;
  attendingDoctor?: Practitioner | null;
  referringDoctor?: Practitioner | null;
  consultants?: Practitioner[];
  additionalNotes?: string | null;
  encounterStatus?: string | null;
  loading?: boolean;
}

export const EncounterDetailsCard: FC<EncounterDetailsCardProps> = ({
  encounter,
  patient,
  attendingDoctor,
  referringDoctor,
  consultants = [],
  additionalNotes,
  encounterStatus,
  loading = false,
}) => {
  const { t } = useTranslation('encounters');

  // Format display values
  const patientName = patient
    ? formatHumanName(patient.name?.[0])
    : t('detailsPage.loading');
  const encounterTypeName =
    encounter.type?.[0]?.coding?.[0]?.display ||
    encounter.type?.[0]?.text ||
    t('detailsPage.unknownType');
  const attendingDoctorName = attendingDoctor
    ? formatHumanName(attendingDoctor.name?.[0])
    : t('detailsPage.notSpecified');
  const referringDoctorName = referringDoctor
    ? formatHumanName(referringDoctor.name?.[0])
    : '-';
  const reasonForVisit =
    encounter.reasonCode?.[0]?.text || t('detailsPage.notSpecified');
  const createdAt = encounter.period?.start
    ? dayjs(encounter.period.start).format('DD/MM/YYYY HH:mm')
    : t('detailsPage.unknown');
  const statusLabel = encounterStatus
    ? t(`statusOptions.${encounterStatus}`, {
        defaultValue: encounterStatus.replace(/-/g, ' '),
      })
    : null;

  return (
    <Card withBorder w="100%" maw={600} p="lg" pos="relative" mx="auto">
      <LoadingOverlay visible={loading} />

      <Text size="lg" fw={600} mb="md">
        {t('detailsPage.encounterDetails')}
      </Text>

      <Stack gap="sm">
        {/* Patient */}
        <Group gap="sm">
          <IconUser size={18} />
          <Box flex={1}>
            <Text size="xs" c="dimmed">
              {t('detailsPage.patientLabel')}
            </Text>
            <Text size="sm" fw={500}>
              {patientName}
            </Text>
          </Box>
        </Group>

        <Divider />

        {/* Encounter Type */}
        <Group gap="sm">
          <IconClipboardText size={18} />
          <Box flex={1}>
            <Text size="xs" c="dimmed">
              {t('detailsPage.encounterTypeLabel')}
            </Text>
            <Badge variant="light" size="md" mt={4}>
              {encounterTypeName}
            </Badge>
          </Box>
        </Group>

        <Divider />

        {/* Encounter Status */}
        <Group gap="sm">
          <IconActivity size={18} />
          <Box flex={1}>
            <Text size="xs" c="dimmed">
              {t('status')}
            </Text>
            {encounterStatus && statusLabel ? (
              <Box mt={4}>
                <StatusBadge status={encounterStatus} label={statusLabel} />
              </Box>
            ) : (
              <Text size="sm" fw={500}>
                -
              </Text>
            )}
          </Box>
        </Group>

        <Divider />

        {/* Attending Doctor */}
        <Group gap="sm">
          <IconUser size={18} />
          <Box flex={1}>
            <Text size="xs" c="dimmed">
              {t('detailsPage.attendingDoctorLabel')}
            </Text>
            <Text size="sm" fw={500}>
              {attendingDoctorName}
            </Text>
          </Box>
        </Group>

        <Divider />

        {/* Additional Consultants */}
        <Group gap="sm" align="flex-start">
          <IconUser size={18} style={{ marginTop: 2 }} />
          <Box flex={1}>
            <Text size="xs" c="dimmed">
              {t('detailsPage.additionalConsultantsLabel')}
            </Text>
            {consultants.length > 0 ? (
              <List size="sm" spacing={4} mt={4}>
                {consultants.map((consultant, index) => (
                  <List.Item key={consultant.id || index}>
                    {formatHumanName(consultant.name?.[0])}
                  </List.Item>
                ))}
              </List>
            ) : (
              <Text size="sm" fw={500}>
                -
              </Text>
            )}
          </Box>
        </Group>

        <Divider />

        {/* Reason for Visit */}
        <Group gap="sm" align="flex-start">
          <IconStethoscope size={18} style={{ marginTop: 2 }} />
          <Box flex={1}>
            <Text size="xs" c="dimmed">
              {t('detailsPage.reasonForVisitLabel')}
            </Text>
            <Text size="sm">{reasonForVisit}</Text>
          </Box>
        </Group>

        <Divider />

        {/* Referring Doctor */}
        <Group gap="sm">
          <IconUser size={18} />
          <Box flex={1}>
            <Text size="xs" c="dimmed">
              {t('detailsPage.referringDoctorLabel')}
            </Text>
            <Text size="sm" fw={500}>
              {referringDoctorName}
            </Text>
          </Box>
        </Group>

        <Divider />

        {/* Created At */}
        <Group gap="sm">
          <IconCalendar size={18} />
          <Box flex={1}>
            <Text size="xs" c="dimmed">
              {t('detailsPage.createdAt')}
            </Text>
            <Text size="sm" fw={500}>
              {createdAt}
            </Text>
          </Box>
        </Group>

        <Divider />

        {/* Additional Notes */}
        <Group gap="sm" align="flex-start">
          <IconNotes size={18} style={{ marginTop: 2 }} />
          <Box flex={1}>
            <Text size="xs" c="dimmed">
              {t('detailsPage.additionalNotesLabel')}
            </Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {additionalNotes || '-'}
            </Text>
          </Box>
        </Group>
      </Stack>
    </Card>
  );
};
